// @ts-nocheck
import React, { useEffect, useState } from "react";
import { FiArrowDown, FiArrowUp, FiChevronDown, FiTrash2 } from "react-icons/fi";

import PostgresSourceConfig from "./PostgresSourceConfig";
import ProcessedModelOutputConfig from "./ProcessedModelOutputConfig";
import ModelOutputVariablePicker from "./ModelOutputVariablePicker";
import DatasetSourceConfig from "./DatasetSourceConfig";
import DataOperationConfig from "./DataOperationConfig";
import ObservationAccuracyConfig from "./ObservationAccuracyConfig";
import SourceDisplayConfig from "./SourceDisplayConfig";
import DataSourcePreviewChart from "./DataSourcePreviewChart";
import ForecastRecordPreview from "../ModelOutput/ForecastRecordPreview";
import { customViewModelColor, fetchPreviewData, fetchLatestForecastS3Key, formatSourceType, isSourceConfigured } from "../../utils/composedForecastUtils";
import type { DataSourceFormState, ForecastSourceType, PreviewData } from "../../types/composedForecast";

import Select from "../ReusableComponents/Select";
// Labelled by the shared map so the form names a source the way the card and detail modal do
const SOURCE_TYPE_OPTIONS: { value: ForecastSourceType; label: string }[] = (
  ["POSTGRES_HISTORICAL", "PROCESSED_MODEL_OUTPUT", "DATASET"] as ForecastSourceType[]
).map((value) => ({ value, label: formatSourceType(value) }));

interface DataSourceRowProps {
  dataSource: DataSourceFormState;
  error?: string;
  /** What the source still needs (from the modal's save); the row opens so the field is in view. */
  configError?: string;
  /** The first model output — the forecast that an observation is scored against, so it can't be one. */
  isBaseModel?: boolean;
  /** Output of the base model (first model output) — tells the observation settings what it holds. */
  baseOutputId?: string;
  /** The base model's variable in use (its saved choice, else its file's first). */
  baseVariation?: string;
  /** Reports this row's variable in use, so the modal can hand the base model's to the observation settings. */
  onResolvedVariation?: (variation: string | undefined) => void;
  onUpdate: (updated: DataSourceFormState) => void;
  onRemove: () => void;
  /** Move the row up or down the list (the list order is the draw order: the top row is drawn on top); unset at that end. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const DataSourceRow: React.FC<DataSourceRowProps> = ({
  dataSource,
  error,
  configError,
  isBaseModel = false,
  baseOutputId,
  baseVariation,
  onResolvedVariation,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}) => {
  // A saved source opens folded so an edit starts on the overview; a freshly added one opens ready to fill in
  const [expanded, setExpanded] = useState(!dataSource.id);
  useEffect(() => {
    if (configError) setExpanded(true);
  }, [configError]);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewS3Key, setPreviewS3Key] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // The variable in use for a model output: the saved choice, else the file's first (what the chart draws)
  const [resolvedVariation, setResolvedVariation] = useState<string | undefined>(undefined);
  const variationInUse = dataSource.variation || resolvedVariation;
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handleSourceTypeChange = (sourceType: ForecastSourceType) => {
    setResolvedVariation(undefined);
    onResolvedVariation?.(undefined);
    onUpdate({
      ...dataSource,
      sourceType,
      // Reset source-specific fields
      parameterId: undefined,
      parameterName: undefined,
      datasourceName: undefined,
      lookbackDays: undefined,
      processedModelOutputId: undefined,
      processedModelOutputName: undefined,
      schemaName: undefined,
      variation: undefined,
      datasetId: undefined,
      datasetName: undefined,
      datasetRecordId: undefined,
      datasetRecordS3Path: undefined,
      datasetRecordFileName: undefined,
      column: undefined,
      curated: undefined,
      operation: null,
      sourceName: dataSource.sourceName, // keep name
    });
    setPreviewData(null);
    setPreviewS3Key(null);
    setPreviewError(null);
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    setPreviewS3Key(null);

    try {
      if (dataSource.sourceType === "PROCESSED_MODEL_OUTPUT") {
        // Use the full ProcessedScatterGraph preview (with percentile bands)
        const s3Key = await fetchLatestForecastS3Key(dataSource.processedModelOutputId);
        setPreviewS3Key(s3Key);
      } else {
        const data = await fetchPreviewData(dataSource.sourceType, dataSource);
        setPreviewData(data);
      }
    } catch (err: any) {
      setPreviewError(err.message || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // The same rule the modal's save applies, so a row that can't be previewed can't be saved either
  const canPreview = () => isSourceConfigured(dataSource);

  return (
    <div className="overflow-hidden rounded-eai-card border border-eai-border">
      {/* Header: the name toggles the body; Remove sits beside it, not inside, so it never also toggles */}
      <div className="flex items-center gap-2 bg-eai-surface-sunken pr-2">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-eai-focus-neutral sm:gap-3"
        >
          <FiChevronDown
            aria-hidden
            className={`h-4 w-4 shrink-0 text-eai-ink-faint transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          <span className="truncate font-outfit text-eai-body-sm font-medium text-eai-ink-muted">
            {dataSource.sourceName || "Untitled Source"}
          </span>
          {/* A pill must never wrap — below sm it is dropped, since Source Type sits right under it */}
          <span className="eai-tag-pill-sm hidden shrink-0 whitespace-nowrap sm:inline-flex">
            {SOURCE_TYPE_OPTIONS.find((o) => o.value === dataSource.sourceType)?.label}
          </span>
          {/* Which forecast gets the bands and the date picker: the first model output in the list */}
          {isBaseModel && (
            <span className="eai-tag-pill-sm hidden shrink-0 whitespace-nowrap border-eai-cta-primary text-eai-cta-primary sm:inline-flex">
              Main forecast
            </span>
          )}
        </button>
        {/* Order = draw order: the top row is drawn on top of the rest */}
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          className="eai-btn-icon shrink-0 disabled:opacity-30"
          aria-label={`Move ${dataSource.sourceName || "source"} up (drawn above)`}
          title="Move up — drawn above the sources below it"
        >
          <FiArrowUp aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          className="eai-btn-icon shrink-0 disabled:opacity-30"
          aria-label={`Move ${dataSource.sourceName || "source"} down (drawn below)`}
          title="Move down — drawn below the sources above it"
        >
          <FiArrowDown aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="eai-btn-icon eai-btn-icon-danger shrink-0"
          aria-label={`Remove ${dataSource.sourceName || "source"}`}
          title="Remove source"
        >
          <FiTrash2 aria-hidden className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-4 py-4 space-y-4">
          {/* Common fields */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="eai-meta-label mb-2 block">
                Source Name <span className="text-eai-danger-text">*</span>
              </label>
              <input
                type="text"
                value={dataSource.sourceName}
                onChange={(e) => onUpdate({ ...dataSource, sourceName: e.target.value })}
                className={`eai-input ${error ? "border-eai-danger-border-strong" : ""}`}
                placeholder="Name this source"
              />
              {error && (
                <p className="mt-1 font-inter text-eai-body-xs text-eai-danger-text">{error}</p>
              )}
            </div>

            <div>
              <label className="eai-meta-label mb-2 block">Source Type</label>
              <Select
                value={dataSource.sourceType}
                onChange={(e) => handleSourceTypeChange(e.target.value as ForecastSourceType)}
                containerClassName="w-full"
              >
                {SOURCE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="eai-meta-label mb-2 block">Multiplier (weight)</label>
                <input
                  type="number"
                  value={dataSource.weight ?? ""}
                  onChange={(e) =>
                    // Kept as typed (0 included); an empty box saves as 1
                    onUpdate({ ...dataSource, weight: e.target.value === "" ? (null as any) : Number(e.target.value) })
                  }
                  step="any"
                  placeholder="1"
                  className="eai-input"
                />
                <p className="mt-1 font-inter text-xs text-eai-ink-faint">
                  Every value from this source is multiplied by this (1 = unchanged), e.g. 1000 for kcfs → cfs.
                </p>
              </div>
              <div>
                <label className="eai-meta-label mb-2 block">Priority</label>
                <input
                  type="number"
                  value={dataSource.priority}
                  onChange={(e) =>
                    onUpdate({ ...dataSource, priority: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                  className="eai-input"
                />
              </div>
            </div>
          </div>

          {/* Source-specific configuration */}
          <div className="border-t border-eai-border pt-4">
            {dataSource.sourceType === "POSTGRES_HISTORICAL" && (
              <PostgresSourceConfig dataSource={dataSource} onUpdate={onUpdate} viewHasForecast={!!baseOutputId} />
            )}
            {dataSource.sourceType === "PROCESSED_MODEL_OUTPUT" && (
              <>
                <ProcessedModelOutputConfig dataSource={dataSource} onUpdate={onUpdate} />
                <ModelOutputVariablePicker
                  outputId={dataSource.processedModelOutputId}
                  value={dataSource.variation}
                  onChange={(variation) => onUpdate({ ...dataSource, variation })}
                  onResolved={(variation) => {
                    setResolvedVariation(variation);
                    onResolvedVariation?.(variation);
                  }}
                />
              </>
            )}
            {dataSource.sourceType === "DATASET" && (
              <DatasetSourceConfig dataSource={dataSource} onUpdate={onUpdate} viewHasForecast={!!baseOutputId} />
            )}
          </div>

          {configError && (
            <p role="alert" className="font-inter text-eai-body-xs text-eai-danger-text">
              {configError}
            </p>
          )}

          {/* Data Operation (pinned-file DATASET only, after a record is picked; a curated dataset is read by column) */}
          {dataSource.sourceType === "DATASET" && !dataSource.curated && dataSource.datasetRecordS3Path && (
            <div className="border-t border-eai-border pt-4">
              <DataOperationConfig
                operation={dataSource.operation ?? null}
                onChange={(operation) => onUpdate({ ...dataSource, operation })}
              />
            </div>
          )}

          {/* Observation: score the base model against this source */}
          {!isBaseModel && (
            <div className="border-t border-eai-border pt-4">
              <ObservationAccuracyConfig
                settings={dataSource.accuracy}
                baseOutputId={baseOutputId}
                baseVariation={baseVariation}
                onChange={(accuracy) => onUpdate({ ...dataSource, accuracy })}
              />
            </div>
          )}

          {/* Display: shown channels (base model) and colours */}
          <div className="border-t border-eai-border pt-4">
            <SourceDisplayConfig
              display={dataSource.display}
              isBaseModel={isBaseModel}
              outputId={dataSource.sourceType === "PROCESSED_MODEL_OUTPUT" ? dataSource.processedModelOutputId : undefined}
              variation={variationInUse}
              isObservation={!isBaseModel && !!dataSource.accuracy?.enabled}
              onChange={(display) => onUpdate({ ...dataSource, display })}
            />
          </div>

          {/* Preview */}
          <div className="border-t border-eai-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="eai-meta-label">Data Preview</span>
              <button
                type="button"
                onClick={handlePreview}
                disabled={!canPreview() || previewLoading}
                className="eai-btn-outline-xs"
              >
                {previewLoading ? "Loading..." : "Preview"}
              </button>
            </div>
            {previewError && (
              <p className="mb-2 font-inter text-eai-body-xs text-eai-danger-text">{previewError}</p>
            )}
            {/* The preview follows the Variable picker, so what's previewed is what's saved */}
            {/* Drawn with the Display settings above, so the preview shows the colours the view will use */}
            {previewS3Key && (
              <ForecastRecordPreview
                s3Key={previewS3Key}
                variation={variationInUse ?? ""}
                display={dataSource.display}
                meanColor={isBaseModel ? customViewModelColor() : dataSource.display?.color || null}
              />
            )}
            {previewData && <DataSourcePreviewChart data={previewData} color={dataSource.display?.color} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceRow;
